// SHER-257: single source of truth for mapping an investigation's personalFields
// to the "roles" the Add Patient duplicate-detection flow cares about. There is
// no stable `type: 'phone'` (the secondary phone is a tenant-custom field with an
// arbitrary name), so roles are resolved by name/label pattern. Both the matching
// layer (View → buildDedupIndex/findDuplicatePatient) and the warning modal
// (display) call this, so they can never disagree on what counts as a phone field.

export interface PersonalFieldLike {
    name: string;
    label?: string;
    type?: string;
}

export interface PersonalFieldRoles {
    /** All phone-typed field names (primary `phone` + tenant-custom secondaries). */
    phone: string[];
    /**
     * SHER-257 (follow-up): all ID-card-typed field names (e.g. a hospice's
     * National ID card). Detected by name/label so centres that record it get
     * ID-card duplicate detection automatically; centres that don't (CHOM) get
     * an empty list and stay phone-only.
     */
    idCard: string[];
    /** First field that looks like a postal address, if any. */
    address?: string;
    /** First field that looks like a referring clinician/doctor, if any. */
    clinician?: string;
}

const PHONE_RE = /phone|tel[eé]fono|t[eé]l[eé]phone/i;
const ADDRESS_RE = /address|direcci[oó]n|adresse/i;
const CLINICIAN_RE = /clinician|doctor|m[eé]dico|m[eé]decin|referring/i;
// Matches "ID card", "National ID", "carte d'identité", "cédula", CNI/DNI/NIN,
// "pièce/numéro d'identité". Deliberately requires a card/national/identity token
// so it never captures the unrelated `health_id` / `automated_health_id` fields.
const ID_CARD_RE = /\bid[\s_-]?card|national[\s_-]?id|carte.*identit|c[eé]dula|\bcni\b|\bdni\b|\bnin\b|(pi[eè]ce|num[eé]ro).*identit|identit[ye].*(card|number|num[eé]ro)/i;

export function resolvePersonalFieldRoles(
    personalFields: PersonalFieldLike[] | null | undefined,
): PersonalFieldRoles {
    const phone: string[] = [];
    const idCard: string[] = [];
    let address: string | undefined;
    let clinician: string | undefined;

    for (const f of personalFields || []) {
        if (!f?.name) continue;
        const hay = `${f.name} ${f.label || ''}`;
        if (f.name === 'phone' || PHONE_RE.test(f.name) || PHONE_RE.test(f.label || '')) {
            phone.push(f.name);
        }
        if (ID_CARD_RE.test(hay)) idCard.push(f.name);
        if (!address && ADDRESS_RE.test(hay)) address = f.name;
        if (!clinician && CLINICIAN_RE.test(hay)) clinician = f.name;
    }

    return { phone, idCard, address, clinician };
}
